import { useGetCitiesQuery, useGetDestinationRegionsQuery } from "@/actions/generics.actions";
import { useCreateTenantLocationMutation } from "@/actions/location.actions";
import { App, Button, Form, type FormInstance, Input, Select, Spin } from "antd";
import axios from "axios";
import debounce from "lodash.debounce";
import { PlusIcon } from "lucide-react";
import type { BaseSelectRef } from "rc-select";
import { useEffect, useMemo, useRef, useState } from "react";

type InventoryLocationFieldsProps = {
  form: FormInstance;
  country?: string;
  onCountryChange: (country?: string) => void;
  countryPlaceholder?: string;
  cityPlaceholder?: string;
  countryRequiredMessage?: string;
  cityRequiredMessage?: string;
  cityOptional?: boolean;
};

/** Selects a known inventory location and lets a tenant add a missing city in context. */
export const InventoryLocationFields = ({
  form,
  country,
  onCountryChange,
  countryPlaceholder = "Search destination",
  cityPlaceholder = "Search city",
  countryRequiredMessage = "Enter destination",
  cityRequiredMessage = "Enter city",
  cityOptional = false,
}: InventoryLocationFieldsProps) => {
  const { message } = App.useApp();
  const [countrySearch, setCountrySearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [cityQuerySearch, setCityQuerySearch] = useState("");
  const citySelectRef = useRef<BaseSelectRef>(null);
  const state = Form.useWatch("state", form) as string | null | undefined;
  const destination = state || country;
  const countriesQuery = useGetDestinationRegionsQuery(countrySearch);
  const citiesQuery = useGetCitiesQuery(destination || "", cityQuerySearch);
  const { refetch: refetchCountries } = countriesQuery;
  const { refetch: refetchCities } = citiesQuery;
  const createLocationMutation = useCreateTenantLocationMutation();
  const updateCountrySearch = useMemo(() => debounce(setCountrySearch, 300), []);
  const updateCityQuerySearch = useMemo(() => debounce(setCityQuerySearch, 300), []);

  useEffect(() => () => {
    updateCountrySearch.cancel();
    updateCityQuerySearch.cancel();
  }, [updateCityQuerySearch, updateCountrySearch]);

  useEffect(() => {
    if (countrySearch) void refetchCountries();
  }, [countrySearch, refetchCountries]);

  useEffect(() => {
    if (country && cityQuerySearch) void refetchCities();
  }, [cityQuerySearch, refetchCities, country]);

  const normalizedCitySearch = citySearch.trim().toLocaleLowerCase();
  const hasExactCity = Boolean(normalizedCitySearch && citiesQuery.data?.some(
    (city) => city.name.trim().toLocaleLowerCase() === normalizedCitySearch,
  ));
  const citySearchSettled = cityQuerySearch.trim().toLocaleLowerCase() === normalizedCitySearch && !citiesQuery.isFetching;
  const canAddCity = Boolean(country?.trim() && citySearch.trim() && citySearchSettled && !hasExactCity);

  const addMissingCity = async () => {
    const nextCountry = country?.trim();
    const nextCity = citySearch.trim();
    if (!nextCountry || !nextCity) return;
    try {
      const result = await createLocationMutation.mutateAsync({
        country: nextCountry,
        state,
        city: nextCity,
      });
      const selectedCity = result.location.city;
      form.setFieldValue("city", selectedCity);
      setCitySearch("");
      setCityQuerySearch("");
      citySelectRef.current?.blur();
      message.success(result.reusedGlobal ? "Selected the existing location" : `${selectedCity} added and selected`);
    } catch (error: unknown) {
      message.error(axios.isAxiosError(error) ? error.response?.data?.error || "Unable to add location" : "Unable to add location");
    }
  };

  return (
    <>
      <Form.Item name="state" hidden><Input /></Form.Item>
      <Form.Item name="country" label="Destination"
        getValueProps={(value: string) => ({ value: state || value })}
        getValueFromEvent={(value: string, option: { country?: string }) => option.country || value}
        rules={[{ required: true, whitespace: true, message: countryRequiredMessage }]}>
        <Select
          aria-label="Destination"
          showSearch
          placeholder={countryPlaceholder}
          loading={countriesQuery.isFetching}
          options={(countriesQuery.data || []).map((item) => ({ value: item.name, label: item.name, country: item.country || item.name, state: item.state }))}
          onSearch={updateCountrySearch}
          onFocus={() => countriesQuery.refetch()}
          onSelect={(value, option) => {
            if (value === destination) return;
            form.setFieldValue("state", option.state || null);
            updateCountrySearch.cancel();
            setCountrySearch("");
            form.setFieldValue("city", undefined);
            setCitySearch("");
            setCityQuerySearch("");
            onCountryChange(option.country);
          }}
          notFoundContent={countriesQuery.isFetching ? <Spin size="small" /> : "No matching destination"}
          filterOption={false}
        />
      </Form.Item>

      <Form.Item
        name="city"
        label={cityOptional ? "City (optional)" : "City"}
        extra={cityOptional ? "Leave blank to offer this activity throughout the destination." : undefined}
        rules={cityOptional ? [] : [{ required: true, whitespace: true, message: cityRequiredMessage }]}
      >
        <Select
          ref={citySelectRef}
          aria-label="City"
          allowClear={cityOptional}
          showSearch
          placeholder={cityPlaceholder}
          loading={citiesQuery.isFetching}
          disabled={!country?.trim()}
          options={(citiesQuery.data || []).map((city) => ({ label: city.name, value: city.name }))}
          searchValue={citySearch}
          onSearch={(value) => {
            setCitySearch(value);
            updateCityQuerySearch(value);
          }}
          onFocus={() => { if (country) citiesQuery.refetch(); }}
          onSelect={() => {
            setCitySearch("");
            setCityQuerySearch("");
          }}
          onClear={() => {
            setCitySearch("");
            setCityQuerySearch("");
          }}
          filterOption={false}
          notFoundContent={citiesQuery.isFetching ? <Spin size="small" /> : "No matching city"}
          dropdownRender={(menu) => (
            <div>
              {menu}
              {canAddCity ? (
                <div className="border-t border-gray-100 p-2">
                  <Button
                    type="text"
                    block
                    className="!flex !justify-start"
                    icon={<PlusIcon size={15} />}
                    loading={createLocationMutation.isPending}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={addMissingCity}
                  >
                    Add “{citySearch.trim()}” to {country}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        />
      </Form.Item>
    </>
  );
};
